import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../../of/home/Dialog'
import { asObj, str } from '../data'
import { useFanslyApi } from '../deps'
import { countryName } from '../settings/settings'
import { formatMills } from '../statistics/statsData'
import { toFanslyError } from '../useFanslyQuery'
import {
  addressClean,
  attributeValid,
  bankCreateBody,
  bankFormOf,
  bankUpdateBody,
  cosmoBody,
  cryptoBody,
  cryptoFormsOf,
  millsFromDollars,
  paxumBody,
  requestProblem,
  stepUpOf,
  validEmail,
  validSan,
  type BankForm,
  type CryptoForm,
  type ProviderTab,
  type StepUp,
} from './payoutData'
import { PAYOUT_COUNTRIES } from './payoutCountries'
import type { PayoutMethod } from './walletData'
import { LoadingBlock, BusyLabel } from '../../components/Spinner'

function Buttons({ busy, disabled, label, onCancel, onSave }: { busy: boolean; disabled: boolean; label: string; onCancel: () => void; onSave: () => void }) {
  const { t } = useTranslation('fansly')
  return (
    <>
      <button type="button" className="lib-btn" onClick={onCancel}>
        {t('cancel')}
      </button>
      <button type="button" className="lib-btn lib-btn--primary fs-dialog-save" disabled={disabled || busy} onClick={onSave}>
        <BusyLabel busy={busy} label={t('saving')}>{label}</BusyLabel>
      </button>
    </>
  )
}

function Problem({ text }: { text: string }) {
  return text ? (
    <div className="fs-danger" role="alert">
      {text}
    </div>
  ) : null
}

/**
 * Fansly's "Confirm it's you": asks Fansly for a code (e-mailed to the
 * creator, or from the authenticator app when 2FA is on) and calls onVerified
 * once it is accepted, or at once when Fansly needs no code.
 */
export function StepUpDialog({ onVerified, onClose }: { onVerified: () => void; onClose: () => void }) {
  const { t } = useTranslation('fanslyWallet')
  const api = useFanslyApi()
  const [step, setStep] = useState<StepUp | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    api
      .post('/crm/settings/twofa/session/create', {})
      .then((r) => {
        const s = stepUpOf(r)
        if (!s) return onVerified()
        setStep(s)
        setBusy(false)
      })
      .catch((e) => {
        setError(toFanslyError(e).message)
        setBusy(false)
      })
  }, [api, onVerified])

  const verify = async () => {
    if (!step || busy) return
    setBusy(true)
    setError('')
    try {
      await api.post('/crm/settings/twofa/session/verify', { token: step.token, code: code.trim(), mode: step.email ? 1 : 0 })
      onVerified()
    } catch (e) {
      setError(toFanslyError(e).message)
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('stepUp.title')} onClose={onClose} actions={<Buttons busy={busy} disabled={!step || !code.trim()} label={t('stepUp.verify')} onCancel={onClose} onSave={() => void verify()} />}>
      <div className="fsw-form">
        {!step && !error && <p className="ofp-modal-note">{t('stepUp.sending')}</p>}
        {step && (
          <>
            <p className="ofp-modal-note">{step.email ? t('stepUp.emailSent', { address: step.address || t('stepUp.theEmail') }) : t('stepUp.appCode')}</p>
            <label className="fs-field">
              {t('stepUp.code')}
              <input className="fs-input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void verify()} autoFocus />
            </label>
            <small className="fsw-hint">{t('stepUp.ownerInbox')}</small>
          </>
        )}
        <Problem text={error} />
      </div>
    </Dialog>
  )
}

const TABS: ProviderTab[] = ['paxum', 'cosmo', 'bank', 'crypto']

/** Fansly's Add Payout Method dialog: Paxum, COSMO, bank (by country) and crypto. Open it after the step-up. */
export function AddMethodDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const { t } = useTranslation('fanslyWallet')
  const api = useFanslyApi()
  const [tab, setTab] = useState<ProviderTab>('paxum')
  const [crypto, setCrypto] = useState<{ eligible: boolean; forms: CryptoForm[] }>({ eligible: false, forms: [] })

  useEffect(() => {
    api
      .get('/crm/creator/payout-crypto/forms')
      .then((r) => setCrypto(cryptoFormsOf(r)))
      .catch(() => setCrypto({ eligible: false, forms: [] }))
  }, [api])

  const tabs = TABS.filter((x) => x !== 'crypto' || crypto.eligible)
  return (
    <Dialog title={t('add.title')} onClose={onClose} wide>
      <div className="fsw-tabs" role="tablist" aria-label={t('add.title')}>
        {tabs.map((x) => (
          <button key={x} type="button" role="tab" aria-selected={tab === x} className={tab === x ? 'is-on' : ''} onClick={() => setTab(x)}>
            {t(`methods.providers.${x === 'crypto' ? 'crypto' : x}`)}
          </button>
        ))}
      </div>
      {tab === 'paxum' && <EmailMethod onClose={onClose} onAdded={onAdded} />}
      {tab === 'cosmo' && <CosmoMethod onClose={onClose} onAdded={onAdded} />}
      {tab === 'bank' && <BankMethod onClose={onClose} onAdded={onAdded} />}
      {tab === 'crypto' && <CryptoMethod forms={crypto.forms} onClose={onClose} onAdded={onAdded} />}
    </Dialog>
  )
}

type MethodProps = { onClose: () => void; onAdded: () => void }

function useCreate(onAdded: () => void) {
  const api = useFanslyApi()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  /** Runs one step; the method counts as added unless the step answers false (more to do). */
  const run = async (fn: (a: typeof api) => Promise<unknown>) => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      if ((await fn(api)) === false) return setBusy(false)
      onAdded()
    } catch (e) {
      setError(toFanslyError(e).message)
      setBusy(false)
    }
  }
  return { busy, error, run }
}

function FormFoot({ busy, disabled, onClose, onSave, label }: { busy: boolean; disabled: boolean; onClose: () => void; onSave: () => void; label?: string }) {
  const { t } = useTranslation('fanslyWallet')
  return (
    <div className="ofp-modal-actions">
      <Buttons busy={busy} disabled={disabled} label={label ?? t('add.submit')} onCancel={onClose} onSave={onSave} />
    </div>
  )
}

function EmailMethod({ onClose, onAdded }: MethodProps) {
  const { t } = useTranslation('fanslyWallet')
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const save = useCreate(onAdded)
  const ok = validEmail(email)
  return (
    <>
      <div className="fsw-form">
        <p className="ofp-modal-note">{t('add.paxumIntro')}</p>
        <label className="fs-field">
          {t('add.paxumEmail')}
          <input className="fs-input" type="email" value={email} placeholder="johndoe@example.com" onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched(true)} autoFocus />
        </label>
        {touched && email && !ok && <small className="fs-danger">{t('add.badEmail')}</small>}
        <small className="fsw-hint">{t('add.sameDetails')}</small>
        <Problem text={save.error} />
      </div>
      <FormFoot busy={save.busy} disabled={!ok} onClose={onClose} onSave={() => void save.run((api) => api.post('/crm/creator/payout-methods/create', paxumBody(email)))} />
    </>
  )
}

function CosmoMethod({ onClose, onAdded }: MethodProps) {
  const { t } = useTranslation('fanslyWallet')
  const [san, setSan] = useState('')
  const [touched, setTouched] = useState(false)
  const save = useCreate(onAdded)
  const ok = validSan(san)
  return (
    <>
      <div className="fsw-form">
        <label className="fs-field">
          {t('add.cosmoSan')}
          <input className="fs-input" inputMode="numeric" value={san} placeholder="1234567890" onChange={(e) => setSan(e.target.value.trim())} onBlur={() => setTouched(true)} autoFocus />
        </label>
        <small className={touched && san && !ok ? 'fs-danger' : 'fsw-hint'}>{t('add.cosmoHint')}</small>
        <small className="fsw-hint">{t('add.sameDetails')}</small>
        <Problem text={save.error} />
      </div>
      <FormFoot busy={save.busy} disabled={!ok} onClose={onClose} onSave={() => void save.run((api) => api.post('/crm/creator/payout-methods/create', cosmoBody(san)))} />
    </>
  )
}

function BankMethod({ onClose, onAdded }: MethodProps) {
  const { t, i18n } = useTranslation('fanslyWallet')
  const api = useFanslyApi()
  const [country, setCountry] = useState('')
  const [bankType, setBankType] = useState('')
  const [form, setForm] = useState<BankForm | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const save = useCreate(onAdded)
  const countries = useMemo(
    () => PAYOUT_COUNTRIES.map((c) => ({ ...c, name: countryName(c.alpha2, i18n.language) })).sort((a, b) => a.name.localeCompare(b.name, i18n.language)),
    [i18n.language],
  )

  const load = async (alpha3: string, type: string) => {
    setLoading(true)
    setLoadError('')
    try {
      const f = bankFormOf(await api.post('/crm/creator/payout-methods/bank', { countryAlpha3: alpha3, currencyCode: '', type }))
      setForm(f)
      setValues(Object.fromEntries(f.attributes.map((a) => [a.type, a.value])))
      setTouched({})
      if (!type && f.bankSelect?.value) setBankType(f.bankSelect.value)
    } catch (e) {
      setForm(null)
      setLoadError(toFanslyError(e).message)
    } finally {
      setLoading(false)
    }
  }

  const pickCountry = (alpha3: string) => {
    setCountry(alpha3)
    setBankType('')
    setForm(null)
    if (alpha3) void load(alpha3, '')
  }

  const attrs = form?.attributes ?? []
  const valid = !!form && attrs.every((a) => attributeValid(a, values[a.type] ?? ''))
  const submit = () =>
    void save.run(async (a) => {
      if (!form) return false
      const updated = await a.post('/crm/creator/payout-methods/bank/update', bankUpdateBody(form, { ...values, ...(form.bankSelect ? { [form.bankSelect.type]: bankType } : {}) }))
      await a.post('/crm/creator/payout-methods/create', bankCreateBody(updated))
    })

  return (
    <>
      <div className="fsw-form">
        <label className="fs-field">
          {t('add.country')}
          <select className="fs-select" value={country} onChange={(e) => pickCountry(e.target.value)}>
            <option value="">{t('add.selectCountry')}</option>
            {countries.map((c) => (
              <option key={c.alpha3} value={c.alpha3}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        {country === 'AUS' && <small className="fsw-hint">{t('add.australia')}</small>}
        {loading && <LoadingBlock pad="8px 0" size={18} label={t('add.loadingBank')} />}
        {loadError && (
          <div className="fs-danger" role="alert">
            {loadError}{' '}
            <button type="button" className="lib-btn" onClick={() => void load(country, bankType)}>
              {t('add.retry')}
            </button>
          </div>
        )}
        {form?.bankSelect && (
          <label className="fs-field">
            {form.bankSelect.label}
            <select
              className="fs-select"
              value={bankType}
              onChange={(e) => {
                setBankType(e.target.value)
                void load(country, e.target.value)
              }}
            >
              <option value="">{t('add.selectOption')}</option>
              {form.bankSelect.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
        )}
        {!loading &&
          attrs.map((a) => {
            const v = values[a.type] ?? ''
            const bad = touched[a.type] && !attributeValid(a, v)
            return (
              <label key={a.type} className="fs-field">
                {a.label}
                {a.options.length ? (
                  <select className="fs-select" value={v} onChange={(e) => setValues((x) => ({ ...x, [a.type]: e.target.value }))} onBlur={() => setTouched((x) => ({ ...x, [a.type]: true }))}>
                    <option value="">{t('add.selectOption')}</option>
                    {a.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input className="fs-input" value={v} placeholder={a.expectedValue} onChange={(e) => setValues((x) => ({ ...x, [a.type]: e.target.value }))} onBlur={() => setTouched((x) => ({ ...x, [a.type]: true }))} />
                )}
                {bad && <small className="fs-danger">{t('add.expected', { value: a.expectedValue || a.label })}</small>}
              </label>
            )
          })}
        {form && <small className="fsw-hint">{t('add.sameDetails')}</small>}
        <Problem text={save.error} />
      </div>
      <FormFoot busy={save.busy} disabled={!valid || loading} onClose={onClose} onSave={submit} />
    </>
  )
}

function CryptoMethod({ forms, onClose, onAdded }: MethodProps & { forms: CryptoForm[] }) {
  const { t } = useTranslation('fanslyWallet')
  const [key, setKey] = useState('')
  const [address, setAddress] = useState('')
  const [confirm, setConfirm] = useState('')
  const [challenge, setChallenge] = useState<{ email: string } | null>(null)
  const [code, setCode] = useState('')
  const save = useCreate(onAdded)
  const form = forms.find((f) => f.key === key) ?? null
  const mismatch = !!confirm && confirm !== address
  const ready = !!form && addressClean(address) && address === confirm

  const sendCode = () =>
    void save.run(async (a) => {
      if (!form) return false
      await a.post('/crm/creator/payout-crypto/validate', { form_key: form.key, address })
      const r = asObj(await a.post('/crm/creator/payout-crypto/email-challenge', { form_key: form.key, address }))
      setChallenge({ email: str(r.email) })
      return false
    })

  const confirmCode = () =>
    void save.run(async (a) => {
      if (!form) return false
      const r = asObj(await a.post('/crm/creator/payout-crypto/email-verify', { form_key: form.key, address, code }))
      if (!str(r.token)) throw new Error(t('add.badCode'))
      await a.post('/crm/creator/payout-methods/create', cryptoBody(form, address, str(r.token)))
    })

  return (
    <>
      <div className="fsw-form">
        {!challenge ? (
          <>
            <label className="fs-field">
              {t('add.coin')}
              <select className="fs-select" value={key} onChange={(e) => setKey(e.target.value)}>
                <option value="">{t('add.selectCoin')}</option>
                {forms.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.network ? `${f.name} · ${f.network}` : f.name}
                  </option>
                ))}
              </select>
            </label>
            {form && (
              <>
                <label className="fs-field">
                  {t('add.address')}
                  <input className="fs-input" value={address} spellCheck={false} onChange={(e) => setAddress(e.target.value)} />
                </label>
                {address && !addressClean(address) && <small className="fs-danger">{t('add.badAddress')}</small>}
                <label className="fs-field">
                  {t('add.addressConfirm')}
                  <input className="fs-input" value={confirm} spellCheck={false} onChange={(e) => setConfirm(e.target.value)} />
                </label>
                {mismatch && <small className="fs-danger">{t('add.addressMismatch')}</small>}
                <small className="fsw-hint">{t('add.cryptoFees')}</small>
              </>
            )}
          </>
        ) : (
          <>
            <p className="ofp-modal-note">{t('add.cryptoCheckEmail', { address: challenge.email || t('stepUp.theEmail') })}</p>
            <label className="fs-field">
              {t('stepUp.code')}
              <input className="fs-input" inputMode="numeric" autoComplete="one-time-code" value={code} placeholder="123456" onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
            </label>
          </>
        )}
        <Problem text={save.error} />
      </div>
      {!challenge ? (
        <FormFoot busy={save.busy} disabled={!ready} onClose={onClose} onSave={sendCode} label={t('add.sendCode')} />
      ) : (
        <FormFoot busy={save.busy} disabled={!/^\d{6}$/.test(code)} onClose={onClose} onSave={confirmCode} label={t('add.confirmAndAdd')} />
      )}
    </>
  )
}

/** Fansly's Request Payout dialog: method, amount (≤ available, ≥ the method's minimum). */
export function RequestPayoutDialog({ methods, available, onClose, onRequested }: { methods: PayoutMethod[]; available: number; onClose: () => void; onRequested: (amount: number) => void }) {
  const { t, i18n } = useTranslation('fanslyWallet')
  const api = useFanslyApi()
  const usable = methods.filter((m) => m.status === 'approved' || m.status === 'idle' || m.status === 'requested')
  const [methodId, setMethodId] = useState(() => (usable.find((m) => m.isDefault) ?? usable[0])?.id ?? '')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const method = usable.find((m) => m.id === methodId) ?? null
  const amount = millsFromDollars(text)
  const problem = requestProblem(amount, available, method)
  const lang = i18n.language
  const amountId = useId()

  const send = async () => {
    if (!method || problem || busy) return
    setBusy(true)
    setError('')
    try {
      await api.post('/crm/creator/payout-requests/create', { payoutMethodId: method.id, amount, confirm: true })
      onRequested(amount)
    } catch (e) {
      setError(toFanslyError(e).message)
      setBusy(false)
    }
  }

  return (
    <Dialog
      title={t('request.title')}
      onClose={onClose}
      actions={<Buttons busy={busy} disabled={!method || !!problem} label={amount && method ? t('request.send', { amount: formatMills(amount, lang) }) : t('request.title')} onCancel={onClose} onSave={() => void send()} />}
    >
      <div className="fsw-form">
        <div className="fsw-request-balance">
          <span>{t('balance.available')}</span>
          <strong>{formatMills(available, lang)}</strong>
        </div>
        <label className="fs-field">
          {t('request.method')}
          <select className="fs-select" value={methodId} onChange={(e) => setMethodId(e.target.value)}>
            {usable.map((m) => (
              <option key={m.id} value={m.id}>
                {[t(`methods.providers.${m.provider}`), m.ending ? t('methods.ending', { digits: m.ending }) : ''].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
        </label>
        <div className="fs-field">
          <label htmlFor={amountId}>{t('request.amount')}</label>
          <div className="fsw-amount">
            <span aria-hidden="true">$</span>
            <input id={amountId} className="fs-input" inputMode="decimal" value={text} placeholder="0.00" onChange={(e) => setText(e.target.value)} autoFocus />
            <button type="button" className="lib-btn" disabled={available <= 0} onClick={() => setText((Math.floor(available / 10) / 100).toFixed(2))}>
              {t('request.max')}
            </button>
          </div>
        </div>
        {problem === 'overBalance' && <small className="fs-danger">{t('request.overBalance')}</small>}
        {problem === 'belowMinimum' && method && <small className="fs-danger">{t('request.minimum', { amount: formatMills(method.minPayout, lang) })}</small>}
        {problem === 'taxLocked' && <small className="fs-danger">{t('request.taxLocked')}</small>}
        {method?.provider === 'crypto' && <small className="fsw-hint">{t('add.cryptoFees')}</small>}
        <small className="fsw-hint">{t('request.timing')}</small>
        <Problem text={error} />
      </div>
    </Dialog>
  )
}
